// Stream settings of a project (mock 2): one form per stream — name, ticket
// prefix, colour, ordering, lanes, WIP, auto-pull, pause, expedite, weekly
// budget and Definition of Ready. The workflow itself is read-only here; it
// is edited in the workflow editor.

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DEFINITION_OF_READY_CHECKS,
  STREAM_COLORS,
  STREAM_ORDER_RULES,
  TICKET_PREFIX_RE,
  type DefinitionOfReadyCheck,
  type Project,
  type StreamColor,
  type StreamDefinition,
  type StreamOrderRule,
  type UpdateStreamRequest,
} from '@goblic/shared'
import { streamsApi } from '../../lib/api-client'
import { useDialogs } from '../dialogs'
import { EmptyState, ErrorNote, Field, LoadingRows, Notice, Panel } from '../ui'
import { ORDER_LABEL, STREAM_COLOR_CLASS, defaultNewTaskLabel } from './board-utils'
import { ClayIcon } from '../goblin-art'

const ORDER_OPTION_LABEL: Record<StreamOrderRule, string> = {
  manual: 'Manual rank (drag to order)',
  severity_age: 'Severity, then age',
  priority_age: 'Priority, then age',
}

const DOR_OPTION_LABEL: Record<DefinitionOfReadyCheck, string> = {
  acceptance_criteria: 'Has acceptance criteria',
  no_open_blockers: 'No open blockers',
  severity_set: 'Has a severity',
}

export function StreamSettingsPanel({ project }: { project: Project }) {
  const streamsQuery = useQuery({
    queryKey: ['project', project.id, 'streams'],
    queryFn: () => streamsApi.list(project.id),
  })
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  if (streamsQuery.isLoading) return <LoadingRows label="Loading streams…" />
  if (streamsQuery.isError) return <ErrorNote error={streamsQuery.error} what="the streams" />

  const streams = [...(streamsQuery.data?.items ?? [])].sort((a, b) => a.position - b.position)
  if (streams.length === 0) {
    return (
      <Panel flush>
        <EmptyState title="No streams yet" art="sorting">
          A stream appears for every workflow in the project’s workflows.
        </EmptyState>
      </Panel>
    )
  }
  const selected = streams.find((s) => s.key === selectedKey) ?? streams[0]!

  return (
    <div className="grid gap-4 lg:grid-cols-[14rem_minmax(0,1fr)]">
      <nav aria-label="Streams" className="flex gap-1 overflow-x-auto lg:flex-col">
        {streams.map((stream) => {
          const active = stream.key === selected.key
          return (
            <button
              key={stream.id}
              type="button"
              aria-current={active}
              onClick={() => setSelectedKey(stream.key)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-control px-3 py-2 text-left text-base ${
                active ? 'bg-sunken font-medium text-ink' : 'text-ink-2 hover:bg-sunken/60'
              }`}
            >
              <span className={`h-2 w-2 shrink-0 rounded-full ${STREAM_COLOR_CLASS[stream.color].dot}`} aria-hidden />
              <span className="truncate">{stream.name}</span>
              {stream.paused ? <span className="text-xs text-signal">paused</span> : null}
            </button>
          )
        })}
      </nav>
      <StreamForm key={selected.id} project={project} stream={selected} />
    </div>
  )
}

function StreamForm({ project, stream }: { project: Project; stream: StreamDefinition }) {
  const queryClient = useQueryClient()
  const { confirm } = useDialogs()
  const [name, setName] = useState(stream.name)
  const [prefix, setPrefix] = useState(stream.ticketPrefix)
  const [buttonLabel, setButtonLabel] = useState(stream.newTaskLabel ?? '')
  const [color, setColor] = useState<StreamColor>(stream.color)
  const [order, setOrder] = useState<StreamOrderRule>(stream.order)
  const [inbox, setInbox] = useState(stream.lanes.inbox)
  const [backlogReady, setBacklogReady] = useState(stream.lanes.backlogReady)
  const [wip, setWip] = useState(stream.wipLimit == null ? '' : String(stream.wipLimit))
  const [countHuman, setCountHuman] = useState(stream.countHumanSteps)
  const [autoPull, setAutoPull] = useState(stream.autoPull)
  const [paused, setPaused] = useState(stream.paused)
  const [expediteOn, setExpediteOn] = useState(stream.expedite != null)
  const [overWip, setOverWip] = useState(String(stream.expedite?.overWip ?? 1))
  const [budget, setBudget] = useState(
    stream.weeklyBudgetCents == null ? '' : String(stream.weeklyBudgetCents / 100),
  )
  const [dor, setDor] = useState<DefinitionOfReadyCheck[]>(stream.definitionOfReady)
  const [saved, setSaved] = useState(false)

  const wipValue = wip.trim() === '' ? null : Number(wip)
  const budgetValue = budget.trim() === '' ? null : Math.round(Number(budget) * 100)
  const errors: string[] = []
  if (!name.trim()) errors.push('Give the stream a name.')
  if (!TICKET_PREFIX_RE.test(prefix)) errors.push('The ticket prefix is 1–10 capital letters or digits, starting with a letter.')
  if (!inbox && !backlogReady) errors.push('Turn on the Inbox or the Backlog + Ready lanes.')
  if (wipValue != null && (!Number.isInteger(wipValue) || wipValue < 1 || wipValue > 100)) {
    errors.push('The WIP limit is a whole number from 1 to 100, or empty for no limit.')
  }
  if (budgetValue != null && (!Number.isFinite(budgetValue) || budgetValue < 0)) {
    errors.push('The weekly budget is an amount of 0 or more, or empty for no budget.')
  }

  const save = useMutation({
    mutationFn: () => {
      const body: UpdateStreamRequest = {
        name: name.trim(),
        ticketPrefix: prefix,
        newTaskLabel: buttonLabel.trim() || null,
        color,
        order,
        lanes: { inbox, backlogReady },
        wipLimit: wipValue,
        countHumanSteps: countHuman,
        autoPull,
        paused,
        expedite: expediteOn ? { severity: 'S1', overWip: Number(overWip) } : null,
        weeklyBudgetCents: budgetValue,
        definitionOfReady: dor,
      }
      return streamsApi.update(project.id, stream.key, body)
    },
    onSuccess: async () => {
      setSaved(true)
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] })
    },
  })

  const remove = useMutation({
    mutationFn: () => streamsApi.remove(project.id, stream.key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project', project.id] }),
  })

  function submit(event: FormEvent): void {
    event.preventDefault()
    if (errors.length === 0) save.mutate()
  }

  async function removeStream(): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Remove the ${stream.name} stream?`,
      message: 'Its workflow no longer exists. Tasks keep their data; they just have no board.',
      confirmLabel: 'Remove',
    })
    if (ok) remove.mutate()
  }

  const toggleDor = (check: DefinitionOfReadyCheck, on: boolean): void =>
    setDor((current) => (on ? [...current.filter((c) => c !== check), check] : current.filter((c) => c !== check)))
  const edited = () => setSaved(false)

  return (
    <form onSubmit={submit} onChange={edited} className="panel space-y-5 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-md font-semibold text-ink">{stream.name}</h2>
        {stream.workflowMissing ? (
          <button type="button" className="btn-danger-quiet btn-sm" disabled={remove.isPending} onClick={() => void removeStream()}>
            Remove
          </button>
        ) : null}
      </div>

      {stream.workflowMissing ? (
        <Notice tone="signal">The workflow of this stream is gone. You can remove the stream.</Notice>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor={`${stream.id}-name`}>
          <input
            id={`${stream.id}-name`}
            className="field-input"
            value={name}
            maxLength={60}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Ticket prefix" htmlFor={`${stream.id}-prefix`} hint={`Cards read ${prefix || 'PREFIX'}-12.`}>
          <input
            id={`${stream.id}-prefix`}
            className="field-input font-mono uppercase"
            value={prefix}
            maxLength={10}
            onChange={(event) => setPrefix(event.target.value.toUpperCase())}
          />
        </Field>
        <Field
          label="Button label"
          htmlFor={`${stream.id}-button-label`}
          hint="The board’s create button. Leave empty for the default."
        >
          <input
            id={`${stream.id}-button-label`}
            className="field-input"
            value={buttonLabel}
            maxLength={40}
            placeholder={defaultNewTaskLabel({
              ...stream,
              order,
              expedite: expediteOn ? { severity: 'S1', overWip: Number(overWip) } : null,
            })}
            onChange={(event) => setButtonLabel(event.target.value)}
          />
        </Field>
      </div>

      <div>
        <p className="mb-1.5 text-base font-medium text-ink">Colour</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Colour">
          {STREAM_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={color === c}
              aria-label={c}
              onClick={() => {
                setColor(c)
                edited()
              }}
              className={`h-7 w-7 rounded-full border-2 ${STREAM_COLOR_CLASS[c].dot} ${
                color === c ? 'border-ink' : 'border-transparent'
              }`}
            />
          ))}
        </div>
      </div>

      <Field label="Workflow" hint="One workflow per stream. Change its steps in the workflow editor.">
        <p className="flex items-center gap-2 text-base text-ink">
          <span className="font-mono">{stream.workflowKey}</span>
          {stream.workflowMissing ? (
            <span className="text-sm text-danger">missing</span>
          ) : (
            <a className="link" href={`/projects/${project.id}/workflows/${stream.workflowKey}/edit`}>
              Open in the editor
            </a>
          )}
        </p>
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Order" htmlFor={`${stream.id}-order`} hint={`Queues are sorted by ${ORDER_LABEL[order]}.`}>
          <select
            id={`${stream.id}-order`}
            className="field-input"
            value={order}
            onChange={(event) => setOrder(event.target.value as StreamOrderRule)}
          >
            {STREAM_ORDER_RULES.map((rule) => (
              <option key={rule} value={rule}>
                {ORDER_OPTION_LABEL[rule]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="WIP limit"
          htmlFor={`${stream.id}-wip`}
          hint="Tasks started at once. Empty means no limit."
        >
          <input
            id={`${stream.id}-wip`}
            className="field-input"
            inputMode="numeric"
            value={wip}
            placeholder="No limit"
            onChange={(event) => setWip(event.target.value.replace(/[^0-9]/g, ''))}
          />
        </Field>
      </div>

      <fieldset className="space-y-2">
        <legend className="mb-1.5 flex items-center gap-2 text-base font-medium text-ink"><ClayIcon name="board" size={24} />Lanes before the flow</legend>
        <Check checked={inbox} onChange={setInbox} label="Inbox" hint="New tasks land here until someone has looked at them." />
        <Check
          checked={backlogReady}
          onChange={setBacklogReady}
          label="Backlog + Ready"
          hint="Ready is the pull lane: the next task is started from there."
        />
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="mb-1.5 flex items-center gap-2 text-base font-medium text-ink"><ClayIcon name="stream" size={24} />Flow</legend>
        <Check checked={autoPull} onChange={setAutoPull} label="Auto-pull" hint="Start the next task whenever there is room under the WIP limit." />
        <Check checked={paused} onChange={setPaused} label="Paused" hint="Nothing new is started, running tasks carry on." />
        <Check
          checked={countHuman}
          onChange={setCountHuman}
          label="Count human steps for WIP"
          hint="Tasks waiting on a person count as in flight."
        />
        <div>
          <Check
            checked={expediteOn}
            onChange={setExpediteOn}
            label="Expedite S1"
            hint="An S1 task skips the queue and may exceed the WIP limit."
          />
          {expediteOn ? (
            <label className="ml-7 mt-2 flex items-center gap-2 text-base text-ink-2">
              May go over the limit by
              <select
                aria-label="Expedite over WIP"
                className="field-input h-8 w-20"
                value={overWip}
                onChange={(event) => setOverWip(event.target.value)}
              >
                {[0, 1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </fieldset>

      <Field
        label="Weekly budget ($)"
        htmlFor={`${stream.id}-budget`}
        hint="When it runs out nothing new is started and the board says why. Empty means no budget."
      >
        <input
          id={`${stream.id}-budget`}
          className="field-input sm:w-48"
          inputMode="decimal"
          value={budget}
          placeholder="No budget"
          onChange={(event) => setBudget(event.target.value.replace(/[^0-9.]/g, ''))}
        />
      </Field>

      <fieldset className="space-y-2">
        <legend className="mb-1.5 flex items-center gap-2 text-base font-medium text-ink"><ClayIcon name="checks" size={24} />Definition of Ready</legend>
        <p className="text-sm text-ink-3">What a task needs before it can enter Ready.</p>
        {DEFINITION_OF_READY_CHECKS.map((check) => (
          <Check
            key={check}
            checked={dor.includes(check)}
            onChange={(on) => toggleDor(check, on)}
            label={DOR_OPTION_LABEL[check]}
          />
        ))}
      </fieldset>

      {errors.length > 0 ? (
        <Notice tone="error">
          <ul className="list-disc pl-4">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {save.isError ? <Notice tone="error">{save.error instanceof Error ? save.error.message : 'Saving failed.'}</Notice> : null}
      {remove.isError ? <Notice tone="error">{remove.error instanceof Error ? remove.error.message : 'Removing failed.'}</Notice> : null}

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={save.isPending || errors.length > 0}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {saved ? <span className="text-sm text-ok">Saved</span> : null}
      </div>
    </form>
  )
}

function Check({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  label: string
  hint?: string
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--action))]"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <span className="block text-base text-ink">{label}</span>
        {hint ? <span className="block text-sm text-ink-3">{hint}</span> : null}
      </span>
    </label>
  )
}
